/*
 * Mode Weiss: Hero-Slideshow im Modus "Ueberblenden" (sections/hero-slideshow-mw.liquid).
 * Stand 2026-09-25. Ersetzt fuer diese Section Dawns <slideshow-component> (global.js).
 *
 * - Crossfade per CSS-Klasse .is-active (Opacity/Transform, GPU), Slides per Grid gestapelt
 * - Autoplay startet erst nach window.load (keine Auswirkung auf LCP/TBT); bis dahin sind
 *   Slide 2+ per CSS display:none, lazy Bilder laden also erst danach
 * - Vor jedem automatischen Wechsel wird das Bild des naechsten Slides dekodiert (kein Aufblitzen)
 * - Pausen ("holds"): Maus-Hover (nur hover:hover + pointer:fine), Tastaturfokus im Slider,
 *   Slider < 25 % sichtbar, Tab im Hintergrund, Block im Theme-Editor ausgewaehlt
 * - Stopp: manuelle Navigation (Pfeile, Punkte, Wischen), Pause-Button, prefers-reduced-motion
 * - Fortschrittsbalken im aktiven Punkt: CSS-Animation, pausiert synchron ueber .is-held
 */
if (!customElements.get('hero-slideshow-mw')) {
  class HeroSlideshowMw extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slideshow');
      if (!this.slider) return;
      this.slides = Array.from(this.slider.querySelectorAll(':scope > .slideshow__slide'));
      if (this.slides.length < 2) return;

      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.speed = (parseFloat(this.slider.dataset.speed) || 6) * 1000;
      this.dots = Array.from(this.querySelectorAll('.slider-counter__link'));
      this.counterCurrent = this.querySelector('.slider-counter--current');
      this.playButton = this.querySelector('.slideshow__autoplay');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.stopped = this.slider.dataset.autoplay !== 'true' || this.reducedMotion.matches;
      this.holds = new Set();
      this.remaining = this.speed;
      this.startedAt = null;
      this.timer = null;
      this.abort = new AbortController();
      const opts = { signal: this.abort.signal };

      this.querySelectorAll('button[name="next"], button[name="previous"]').forEach((button) => {
        button.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.userNavigate(button.name === 'next' ? this.index + 1 : this.index - 1);
          },
          opts
        );
      });
      this.dots.forEach((dot, i) => {
        dot.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.userNavigate(i);
          },
          opts
        );
      });
      if (this.playButton) {
        this.playButton.addEventListener('click', () => (this.stopped ? this.resume() : this.stop()), opts);
      }

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.slider.addEventListener('mouseenter', () => this.hold('hover'), opts);
        this.slider.addEventListener('mouseleave', () => this.release('hover'), opts);
      }
      this.addEventListener(
        'focusin',
        (event) => {
          if (this.playButton && this.playButton.contains(event.target)) this.release('focus');
          else this.hold('focus');
        },
        opts
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (!this.contains(event.relatedTarget)) this.release('focus');
        },
        opts
      );

      // Wischen (passiv, blockiert kein Scrollen)
      this.slider.addEventListener(
        'touchstart',
        (event) => {
          const t = event.touches[0];
          this.touchX = t.clientX;
          this.touchY = t.clientY;
        },
        { passive: true, signal: this.abort.signal }
      );
      this.slider.addEventListener(
        'touchend',
        (event) => {
          if (this.touchX == null) return;
          const t = event.changedTouches[0];
          const dx = t.clientX - this.touchX;
          const dy = t.clientY - this.touchY;
          this.touchX = null;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            this.userNavigate(dx < 0 ? this.index + 1 : this.index - 1);
          }
        },
        { passive: true, signal: this.abort.signal }
      );

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => (entry.isIntersecting ? this.release('offscreen') : this.hold('offscreen')),
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }
      document.addEventListener(
        'visibilitychange',
        () => (document.hidden ? this.hold('hidden') : this.release('hidden')),
        opts
      );
      this.reducedMotion.addEventListener(
        'change',
        () => {
          if (this.reducedMotion.matches) this.stop();
        },
        opts
      );

      if (window.Shopify && window.Shopify.designMode) {
        // Vor Dawns theme-editor.js abfangen (erwartet <slideshow-component>)
        window.addEventListener(
          'shopify:block:select',
          (event) => {
            if (!this.contains(event.target)) return;
            event.stopPropagation();
            const i = this.slides.indexOf(event.target);
            if (i < 0) return;
            this.hold('editor');
            this.goTo(i, true);
          },
          { capture: true, signal: this.abort.signal }
        );
        window.addEventListener(
          'shopify:block:deselect',
          (event) => {
            if (!this.contains(event.target)) return;
            event.stopPropagation();
            this.release('editor');
          },
          { capture: true, signal: this.abort.signal }
        );
      }

      this.updatePlayButton();
      const ready = () => {
        this.classList.add('is-ready');
        this.sync(true);
      };
      if (document.readyState === 'complete') setTimeout(ready, 0);
      else window.addEventListener('load', () => setTimeout(ready, 0), { once: true, signal: this.abort.signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.timer);
    }

    hold(reason) {
      if (this.holds.has(reason)) return;
      this.holds.add(reason);
      this.sync(false);
    }

    release(reason) {
      if (!this.holds.delete(reason)) return;
      this.sync(false);
    }

    // Timer + CSS-Zustand abgleichen. restart = volle Laufzeit fuer den aktuellen Slide.
    sync(restart) {
      clearTimeout(this.timer);
      if (this.startedAt !== null) {
        this.remaining -= performance.now() - this.startedAt;
        this.startedAt = null;
      }
      if (restart) this.remaining = this.speed;

      if (this.stopped) {
        this.classList.remove('is-playing', 'is-held');
        this.slider.setAttribute('aria-live', 'polite');
        return;
      }
      this.classList.add('is-playing');
      this.slider.setAttribute('aria-live', 'off');

      if (this.holds.size > 0 || !this.classList.contains('is-ready')) {
        this.classList.add('is-held');
        return;
      }
      this.classList.remove('is-held');
      this.startedAt = performance.now();
      this.timer = setTimeout(() => this.advance(), Math.max(this.remaining, 0));
    }

    async advance() {
      this.startedAt = null;
      await this.goTo(this.index + 1, false);
      this.sync(true);
    }

    userNavigate(i) {
      this.stop();
      this.goTo(i, true);
    }

    stop() {
      this.stopped = true;
      this.updatePlayButton();
      this.sync(true);
    }

    resume() {
      this.stopped = false;
      this.updatePlayButton();
      this.sync(true);
    }

    updatePlayButton() {
      if (!this.playButton) return;
      this.playButton.classList.toggle('slideshow__autoplay--paused', this.stopped);
      const label = this.stopped ? this.playButton.dataset.labelPlay : this.playButton.dataset.labelPause;
      if (label) this.playButton.setAttribute('aria-label', label);
    }

    async goTo(target, byUser) {
      const count = this.slides.length;
      const i = ((target % count) + count) % count;
      if (i === this.index) return;
      this.classList.add('is-ready');

      if (!byUser) {
        const img = this.slides[i].querySelector('img');
        if (img && !img.complete && img.decode) {
          await Promise.race([img.decode().catch(() => {}), new Promise((r) => setTimeout(r, 3000))]);
        }
      }

      const previous = this.slides[this.index];
      const next = this.slides[i];
      previous.classList.remove('is-active');
      previous.setAttribute('aria-hidden', 'true');
      previous.inert = true;
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
      next.inert = false;
      this.index = i;

      this.dots.forEach((dot, n) => {
        dot.classList.toggle('slider-counter__link--active', n === i);
        if (n === i) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.counterCurrent) this.counterCurrent.textContent = i + 1;
    }
  }

  customElements.define('hero-slideshow-mw', HeroSlideshowMw);
}
